import type {
  AlphaRunAcceptedResponse,
  AlphaRunsResponse,
  AlphaStylePick,
} from "@nlc/shared-types";

import { MBC_AI_API_URL } from "@/features/request/mbc-review/config";

export class AlphaApiError extends Error {
  readonly code: string | undefined;
  readonly status: number;

  constructor(status: number, code?: string) {
    super(code ?? `HTTP ${status}`);
    this.code = code;
    this.status = status;
  }
}

function requestUrl(requestId: number, suffix: string): string {
  return `${MBC_AI_API_URL}/alpha/requests/${requestId}${suffix}`;
}

export async function getAlphaRuns(
  requestId: number,
): Promise<AlphaRunsResponse> {
  const response = await fetch(requestUrl(requestId, "/runs"));

  if (!response.ok) throw new AlphaApiError(response.status);

  return (await response.json()) as AlphaRunsResponse;
}

export async function createAlphaCreation({
  idempotencyKey,
  picks = [],
  requestId,
}: {
  idempotencyKey: string;
  picks?: AlphaStylePick[];
  requestId: number;
}): Promise<AlphaRunAcceptedResponse> {
  const response = await fetch(requestUrl(requestId, "/creations"), {
    body: JSON.stringify({ picks, threadId: String(requestId) }),
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    method: "POST",
  });

  return readAcceptedRun(response);
}

export async function createAlphaBatch({
  creationId,
  idempotencyKey,
  picks = [],
  prompt,
  requestId,
}: {
  creationId: string;
  idempotencyKey: string;
  picks?: AlphaStylePick[];
  prompt: string;
  requestId: number;
}): Promise<AlphaRunAcceptedResponse> {
  const response = await fetch(
    requestUrl(requestId, `/creations/${creationId}/batches`),
    {
      body: JSON.stringify({ picks, prompt }),
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      method: "POST",
    },
  );

  return readAcceptedRun(response);
}

async function readAcceptedRun(
  response: Response,
): Promise<AlphaRunAcceptedResponse> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;

    throw new AlphaApiError(response.status, body?.error);
  }

  return (await response.json()) as AlphaRunAcceptedResponse;
}
